import type { Principal, PrincipalAndRole, Role } from '../../../commonTypes.tsx';
import type { SpecificAccessDetailsProps } from '../specificAccessDetails/SpecificAccessDetails.tsx';
import type { SpecificAccessRowProps } from '../specificAccessDetails/specificAccessRow/SpecificAccessRow.tsx';
import type { PrincipalsMap } from '../../../../controller/principalsMap.ts';

export function getNonNullSpecificAccessPrincipals(
	specificAccessDetails?: SpecificAccessDetailsProps,
): SpecificAccessRowProps[] {
	return (specificAccessDetails?.principals ?? []).filter(
		(p): p is NonNullable<typeof p> => p !== null,
	);
}

export type SpecificAccessLiveDelta = {
	removeAll: boolean;
	removals: Set<string>;
	reassignments: Map<string, Role>;
};

export function createEmptySpecificAccessLiveDelta(): SpecificAccessLiveDelta {
	return {
		removeAll: false,
		removals: new Set(),
		reassignments: new Map(),
	};
}

export function applySpecificAccessLiveDelta({
	basePrincipalsMap,
	currentUserId,
	liveDelta,
}: {
	basePrincipalsMap: PrincipalsMap;
	currentUserId: string;
	liveDelta: SpecificAccessLiveDelta;
}): PrincipalsMap {
	const map: PrincipalsMap = new Map(basePrincipalsMap);

	if (liveDelta.removeAll) {
		for (const id of map.keys()) {
			if (id !== currentUserId) {
				map.delete(id);
			}
		}
	} else {
		for (const [id, role] of liveDelta.reassignments) {
			const existingEntry = map.get(id);
			if (existingEntry) {
				map.set(id, { ...existingEntry, role, isNewOrUpdatedPrincipal: true });
			}
		}

		for (const id of liveDelta.removals) {
			map.delete(id);
		}
	}

	return map;
}

export function createPrincipalsMapFromSpecificAccessRows({
	principals,
}: {
	principals: ReadonlyArray<{
		principalDetails: {
			principal: Principal;
			role: Role;
		};
	}>;
}): PrincipalsMap {
	const map: PrincipalsMap = new Map();
	for (const row of principals) {
		map.set(row.principalDetails.principal.id, {
			role: row.principalDetails.role,
			principal: row.principalDetails.principal,
			isNewOrUpdatedPrincipal: false,
		});
	}
	return map;
}

export function removeAllPrincipalsFromLiveDelta({
	liveDelta,
}: {
	liveDelta: SpecificAccessLiveDelta;
}): SpecificAccessLiveDelta {
	return {
		...liveDelta,
		removeAll: true,
		removals: new Set(),
		reassignments: new Map(),
	};
}

export function removePrincipalFromLiveDelta({
	liveDelta,
	principalId,
}: {
	liveDelta: SpecificAccessLiveDelta;
	principalId: string;
}): SpecificAccessLiveDelta {
	const removals = new Set(liveDelta.removals);
	removals.add(principalId);
	const reassignments = new Map(liveDelta.reassignments);
	reassignments.delete(principalId);
	return { ...liveDelta, removals, reassignments };
}

export function reassignPrincipalInLiveDelta({
	liveDelta,
	principalId,
	role,
}: {
	liveDelta: SpecificAccessLiveDelta;
	principalId: string;
	role: Role;
}): SpecificAccessLiveDelta {
	return {
		...liveDelta,
		reassignments: new Map(liveDelta.reassignments).set(principalId, role),
	};
}

export function shouldIncludeInvitesForSpecificAccessLiveMutation({
	principals,
	liveDelta,
	principalsPendingAccess,
	shouldMutationIncludeInvites,
}: {
	principals: PrincipalsMap;
	liveDelta: SpecificAccessLiveDelta;
	principalsPendingAccess: PrincipalAndRole[];
	shouldMutationIncludeInvites: (args: {
		principals: PrincipalsMap;
		removals: Set<string>;
		principalsPendingAccess: PrincipalAndRole[];
		removeAll: boolean;
	}) => boolean;
}): boolean {
	return shouldMutationIncludeInvites({
		principals,
		removals: liveDelta.removals,
		principalsPendingAccess,
		removeAll: liveDelta.removeAll,
	});
}
